// ─── Sprint 093 — Roamer Backpack™ Service ───────────────────────────────────
// The Backpack is an inbox. Every captured item enters here first, gets
// processed by BackpackProcessingEngine, then moves to the Vault.
//
// TODO: CLOUD INTEGRATION — when Supabase credentials are provided:
//   1. Replace load/save with POST /api/backpack, GET /api/backpack (API server)
//   2. API server uses SUPABASE_SERVICE_ROLE_KEY to read/write `backpack_items` table
//   3. fileDataUrl → Cloudflare R2 URL after upload via POST /api/backpack/upload
//   4. Processing pipeline moves server-side (OpenAI + Supabase updates)

import type { BackpackItem, BackpackItemType, BackpackItemSubtype, ProcessingStepResult } from '@/types/roamerBackpack';
import { PIPELINE_STEPS } from '@/types/roamerBackpack';
import { storageManager } from '@/services/storage/StorageManager';
import * as FileStorageService from '@/services/fileStorage/FileStorageService';

const STORAGE_KEY = 'nomad:backpack:v1';
export const BACKPACK_CHANGED_EVENT = 'nomad:backpack-changed';

interface BackpackStore {
  version: 1;
  items: BackpackItem[];
}

function generateId(): string {
  return `bp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function now(): string {
  return new Date().toISOString();
}

function loadStore(): BackpackStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, items: [] };
    return JSON.parse(raw) as BackpackStore;
  } catch {
    return { version: 1, items: [] };
  }
}

function saveStore(store: BackpackStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch { /* quota exceeded — large file base64 may cause this */ }
  // Write-through to cloud (api-server → Supabase); localStorage stays the
  // synchronous cache/offline layer. Queues offline, never throws.
  storageManager.push('backpack', 'store', store);
}

/**
 * Boot hydration: adopt the cloud copy only if strictly newer than the last
 * local write (gateway enforces this). Emits BACKPACK_CHANGED_EVENT so pages
 * re-read via the normal service functions — no direct Supabase access.
 */
export async function hydrateBackpackFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<BackpackStore>('backpack', 'store');
  if (!cloud || cloud.version !== 1 || !Array.isArray(cloud.items)) return false;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud)); } catch { return false; }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(BACKPACK_CHANGED_EVENT, { detail: { source: 'cloud' } }));
  }
  return true;
}

function buildEmptySteps(): ProcessingStepResult[] {
  return PIPELINE_STEPS.map((label, i) => ({
    step: i + 1,
    label,
    status: 'waiting' as const,
    detail: null,
  }));
}

/** Add a URL or text item to the backpack. Returns the new item. */
export function addUrlItem(url: string, subtype: BackpackItemSubtype): BackpackItem {
  const store = loadStore();
  const item: BackpackItem = {
    id: generateId(),
    type: 'url',
    subtype,
    title: url,
    contentText: url,
    fileDataUrl: null,
    fileName: null,
    fileSize: null,
    fileMimeType: null,
    processingStatus: 'pending',
    processingStep: 0,
    processingSteps: buildEmptySteps(),
    processingError: null,
    createdAt: now(),
    processedAt: null,
    vaultItemId: null,
  };
  store.items.unshift(item);
  saveStore(store);
  return item;
}

/** Add a text-paste item to the backpack. Returns the new item. */
export function addTextItem(text: string): BackpackItem {
  const store = loadStore();
  const item: BackpackItem = {
    id: generateId(),
    type: 'text',
    subtype: 'text_paste',
    title: text.length > 60 ? text.slice(0, 57) + '…' : text,
    contentText: text,
    fileDataUrl: null,
    fileName: null,
    fileSize: null,
    fileMimeType: null,
    processingStatus: 'pending',
    processingStep: 0,
    processingSteps: buildEmptySteps(),
    processingError: null,
    createdAt: now(),
    processedAt: null,
    vaultItemId: null,
  };
  store.items.unshift(item);
  saveStore(store);
  return item;
}

/**
 * @deprecated Use {@link addFileItemAsync} instead. This sync variant stores
 * the file as a base64 data URL in localStorage (localStorage quota risk).
 * Kept for backward compatibility only.
 *
 * TODO: Remove once all callers have migrated to addFileItemAsync.
 */
export function addFileItem(params: {
  fileDataUrl: string;
  fileName: string;
  fileSize: number;
  fileMimeType: string;
  type: BackpackItemType;
  subtype: BackpackItemSubtype;
}): BackpackItem {
  const store = loadStore();
  const item: BackpackItem = {
    id: generateId(),
    type: params.type,
    subtype: params.subtype,
    title: params.fileName,
    contentText: null,
    fileDataUrl: params.fileDataUrl,
    fileName: params.fileName,
    fileSize: params.fileSize,
    fileMimeType: params.fileMimeType,
    processingStatus: 'pending',
    processingStep: 0,
    processingSteps: buildEmptySteps(),
    processingError: null,
    createdAt: now(),
    processedAt: null,
    vaultItemId: null,
  };
  store.items.unshift(item);
  saveStore(store);
  return item;
}

/**
 * @deprecated Use {@link addVoiceMemoItemAsync} instead. This sync variant stores
 * audio as a base64 data URL in localStorage (localStorage quota risk).
 * Kept for backward compatibility only.
 *
 * TODO: Remove once all callers have migrated to addVoiceMemoItemAsync.
 */
export function addVoiceMemoItem(audioDataUrl: string, durationSec: number): BackpackItem {
  const store = loadStore();
  const ts = new Date();
  const label = `Voice Memo ${ts.toLocaleDateString()} ${ts.toLocaleTimeString()}`;
  const item: BackpackItem = {
    id: generateId(),
    type: 'voice_memo',
    subtype: 'voice_note',
    title: label,
    contentText: null,
    fileDataUrl: audioDataUrl,
    fileName: `voice-memo-${Date.now()}.webm`,
    fileSize: null,
    fileMimeType: 'audio/webm',
    processingStatus: 'pending',
    processingStep: 0,
    processingSteps: buildEmptySteps(),
    processingError: null,
    createdAt: now(),
    processedAt: null,
    vaultItemId: null,
  };
  // Store duration in a custom metadata slot via title
  item.title = `${label} (${Math.round(durationSec)}s)`;
  store.items.unshift(item);
  saveStore(store);
  return item;
}

/**
 * Add a file item to the backpack with Cloudflare R2 cloud storage.
 * Uploads the file to R2 first; falls back to base64 in localStorage on failure.
 * Returns the saved BackpackItem (async — awaits the R2 upload).
 */
export async function addFileItemAsync(
  file: File,
  type: BackpackItemType = 'file',
  subtype: BackpackItemSubtype = 'document',
): Promise<BackpackItem> {
  const store = loadStore();

  // Attempt R2 upload for durable cloud storage
  let fileDataUrl: string | null = null;
  let r2FileId: number | null = null;

  try {
    const result = await FileStorageService.uploadAndGetUrl(file, file.name, file.type || undefined);
    r2FileId   = result.record.id;
    fileDataUrl = result.url; // signed https:// URL for display
  } catch {
    // R2 unavailable — fall back to base64 (local only)
    fileDataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const item: BackpackItem = {
    id:               generateId(),
    type,
    subtype,
    title:            file.name,
    contentText:      null,
    fileDataUrl,
    r2FileId,
    fileName:         file.name,
    fileSize:         file.size,
    fileMimeType:     file.type || 'application/octet-stream',
    processingStatus: 'pending',
    processingStep:   0,
    processingSteps:  buildEmptySteps(),
    processingError:  null,
    createdAt:        now(),
    processedAt:      null,
    vaultItemId:      null,
  };
  store.items.unshift(item);
  saveStore(store);
  return item;
}

/**
 * Add a voice memo item to the backpack with Cloudflare R2 cloud storage.
 * Uploads the recorded blob to R2; falls back to base64 on failure.
 */
export async function addVoiceMemoItemAsync(
  audioBlob: Blob,
  durationSec: number,
): Promise<BackpackItem> {
  const store = loadStore();
  const ts    = new Date();
  const label = `Voice Memo ${ts.toLocaleDateString()} ${ts.toLocaleTimeString()} (${Math.round(durationSec)}s)`;
  const fileName = `voice-memo-${Date.now()}.webm`;

  let fileDataUrl: string | null = null;
  let r2FileId: number | null = null;

  try {
    const result = await FileStorageService.uploadAndGetUrl(audioBlob, fileName, 'audio/webm');
    r2FileId    = result.record.id;
    fileDataUrl = result.url;
  } catch {
    // R2 unavailable — fall back to base64 (local only)
    fileDataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });
  }

  const item: BackpackItem = {
    id:               generateId(),
    type:             'voice_memo',
    subtype:          'voice_note',
    title:            label,
    contentText:      null,
    fileDataUrl,
    r2FileId,
    fileName,
    fileSize:         null,
    fileMimeType:     'audio/webm',
    processingStatus: 'pending',
    processingStep:   0,
    processingSteps:  buildEmptySteps(),
    processingError:  null,
    createdAt:        now(),
    processedAt:      null,
    vaultItemId:      null,
  };
  store.items.unshift(item);
  saveStore(store);
  return item;
}

/** Update a backpack item (used by the processing engine to write progress). */
export function updateBackpackItem(id: string, updates: Partial<BackpackItem>): void {
  const store = loadStore();
  const idx = store.items.findIndex(i => i.id === id);
  if (idx === -1) return;
  store.items[idx] = { ...store.items[idx], ...updates };
  saveStore(store);
}

/** Return all items, newest first. */
export function getAllBackpackItems(): BackpackItem[] {
  return [...loadStore().items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** Items still in the inbox (not yet moved to vault). */
export function getPendingItems(): BackpackItem[] {
  return getAllBackpackItems().filter(i => i.processingStatus !== 'complete');
}

/** Count of new/unprocessed items (badge number). */
export function getPendingCount(): number {
  return getPendingItems().length;
}

/** Count by subtype for pending items (for the Backpack summary panel). */
export function getPendingCountBySubtype(): Partial<Record<BackpackItemSubtype, number>> {
  const counts: Partial<Record<BackpackItemSubtype, number>> = {};
  for (const item of getPendingItems()) {
    counts[item.subtype] = (counts[item.subtype] ?? 0) + 1;
  }
  return counts;
}

/** Remove a single item from the backpack. */
export function deleteBackpackItem(id: string): void {
  const store = loadStore();
  store.items = store.items.filter(i => i.id !== id);
  saveStore(store);
}

/** Clear all backpack items (Factory Reset). */
export function clearBackpack(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  storageManager.remove('backpack', 'store');
}
